/**
 * ModMed-specific integration metadata.
 *
 * Runtime DOM extraction lives in `clients/extension/src/integrations/modmed/`.
 * This file is the wire-safe, no-DOM-required source of truth for:
 *   - the provider id used everywhere
 *   - host allowlist (production + sandbox)
 *   - URL-derivable page-type detection
 *
 * Anything that needs DOM access (extraction, anchor finding, button
 * injection) must NOT live here — that's the extension's responsibility.
 */

import type { EmrPageType, IntegrationMetadata } from './types';

export const MODMED_INTEGRATION_ID = 'modmed' as const;

/**
 * Host suffixes ModMed is known to serve EMA from.
 *
 * These are a detection FAST-PATH, not a gate: the bridge also recognizes EMA
 * structurally (URL shape + DOM signature), so per-org custom / vanity domains
 * — e.g. `sccdc.ema.md` — work without being listed here. `.ema.md` is EMA's
 * canonical per-practice domain; `.modmed.com` / `.modmedical.com` are the
 * corporate hosts; the sandbox suffix backs dev/staging testing.
 */
export const MODMED_HOST_SUFFIXES = {
  production: ['.modmed.com', '.modmedical.com', '.ema.md'],
  sandbox: ['.m2integrationtest.com'],
} as const;

export const modmedIntegrationMetadata: IntegrationMetadata = {
  id: MODMED_INTEGRATION_ID,
  name: 'ModMed',
  description: 'Real-time patient context from ModMed EMA',
  enabled: true,
};

/**
 * Host-independent structural test for "this is a ModMed EMA page".
 *
 * EMA is an Angular SPA served under an `/ema/` path segment and hash-routed:
 *   `https://<host>/ema/web/practice/staff/#/practice/staff/patient/444900/chart/overview`
 *
 * We recognize it by URL SHAPE rather than hostname so per-org custom / vanity
 * domains match without being enumerated. Deliberately defensive: a false
 * positive is harmless — extraction still requires ModMed's patient DOM, which
 * no other app has, so nothing is captured or injected off-target.
 */
export function isModmedUrlShape(rawUrl: string): boolean {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return false;
  }

  // EMA app path prefix — present on every deployment we've observed.
  if (/(^|\/)ema(\/|$)/.test(url.pathname)) return true;

  // EMA hash routes — the patient chart, or the staff practice shell.
  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : '';
  const segments = hash.split('/').filter(Boolean);
  if (segments.includes('patient') && segments.includes('chart')) return true;
  if (segments.includes('practice') && segments.includes('staff')) return true;

  return false;
}

/**
 * Pure URL → page-type classifier. ModMed uses hash routing:
 *   `/practice/staff/#/practice/staff/patient/444900/chart/overview`
 *
 * The page is in the hash, not the pathname. The patient id sits between
 * `/patient/` and `/chart/`; the chart sub-page (`overview`, `billing`, …)
 * follows it.
 *
 * Gating is by URL SHAPE (`isModmedUrlShape`), NOT hostname — so a client on a
 * custom EMA domain classifies correctly. Returning `null` for `emrPatientId`
 * is a strong signal that this is NOT a patient-contextual page — the bridge
 * will skip extraction.
 */
export function classifyModmedUrl(rawUrl: string): {
  isSupported: boolean;
  pageType: EmrPageType;
  emrPatientId: string | null;
} {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return { isSupported: false, pageType: 'unknown', emrPatientId: null };
  }

  // Not a ModMed EMA route at all → nothing to classify.
  if (!isModmedUrlShape(rawUrl)) {
    return { isSupported: false, pageType: 'unknown', emrPatientId: null };
  }

  // Hash routing — strip leading `#/` and split on `/`.
  const hashPath = url.hash.startsWith('#') ? url.hash.slice(1) : '';
  const segments = hashPath.split('/').filter(Boolean);

  const patientIdx = segments.indexOf('patient');
  const emrPatientId =
    patientIdx >= 0 && patientIdx + 1 < segments.length
      ? (segments[patientIdx + 1] ?? null)
      : null;

  if (emrPatientId === null) {
    return { isSupported: true, pageType: 'unknown', emrPatientId: null };
  }

  const chartIdx = segments.indexOf('chart');
  const chartSubPage =
    chartIdx >= 0 && chartIdx + 1 < segments.length ? segments[chartIdx + 1] : undefined;

  const pageType: EmrPageType = (() => {
    switch (chartSubPage) {
      case 'overview':
        return 'patient_account';
      case 'billing':
        return 'billing';
      case 'claims':
        return 'claims';
      default:
        return 'patient_chart';
    }
  })();

  return { isSupported: true, pageType, emrPatientId };
}
